import { useState, useEffect, useMemo, useRef } from "react";
import type { ChangeEvent } from "react";
import { Input } from "antd";
import type { InputRef } from "antd";
import { formatSlotDisplay } from "../../../../common/utils/slot";
import {
  EXPIRY_DATE_ERROR,
  getMinExpiryDate,
  isExpiryDateAllowed,
} from "../../../../common/utils/expiryDate";
import * as Styles from "./styles";

interface SlotPosition {
  x: number;
  y: number;
  z: number;
}

interface LegacyInwardModalProps {
  gate: {
    slot: SlotPosition;
  };
  onSubmit: (
    containerId: string,
    skuId: string,
    unitCount: number,
    /** Optional expiry, ISO `YYYY-MM-DD`. Empty when the operator leaves the field blank. */
    expirationDate: string
  ) => void;
  submitting: boolean;
  errorMessage?: string | null;
  initialSkuId?: string;
  initialUnitCount?: string;
  isStaticSku?: boolean;
  lockSkuId?: boolean;
  lockUnitCount?: boolean;
}

export default function LegacyInwardModal({
  gate,
  onSubmit,
  submitting,
  errorMessage,
  initialSkuId,
  initialUnitCount,
  isStaticSku = false,
  lockSkuId = false,
  lockUnitCount = false,
}: LegacyInwardModalProps) {
  const [containerId, setContainerId] = useState("");
  const [skuId, setSkuId] = useState(initialSkuId ?? "");
  const [unitCount, setUnitCount] = useState(initialUnitCount ?? "");
  const [expirationDate, setExpirationDate] = useState("");
  const [animationStarted, setAnimationStarted] = useState(false);
  const containerIdInputRef = useRef<InputRef>(null);

  const slotLabel = formatSlotDisplay(gate.slot);
  const containerName = isStaticSku ? "Case" : "Tote";
  const containerLabel = `${containerName} ID`;
  const containerNoun = containerName.toLowerCase();

  // "Today" is fixed for the life of the modal — it is open for seconds, not across midnight.
  const minExpiryDate = useMemo(() => getMinExpiryDate(new Date()), []);
  const isExpiryDateValid = isExpiryDateAllowed(expirationDate, minExpiryDate);

  const isFormValid =
    containerId.trim() !== "" &&
    skuId.trim() !== "" &&
    unitCount.trim() !== "" &&
    Number(unitCount) > 0 &&
    isExpiryDateValid;

  useEffect(() => {
    setSkuId(initialSkuId ?? "");
  }, [initialSkuId]);

  useEffect(() => {
    setUnitCount(initialUnitCount ?? "");
  }, [initialUnitCount]);

  useEffect(() => {
    const timer = setTimeout(() => {
      containerIdInputRef.current?.focus();
    }, 300);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setAnimationStarted(true), 200);
    return () => clearTimeout(timer);
  }, []);

  const handleDone = () => {
    if (!isFormValid || submitting) {
      return;
    }

    const enteredExpirationDate = expirationDate.trim();

    // The input's `min` greys out earlier dates in the native picker, but a typed or pasted
    // value still reaches here.
    if (!isExpiryDateAllowed(enteredExpirationDate, minExpiryDate)) {
      return;
    }

    onSubmit(containerId.trim(), skuId.trim(), Number(unitCount), enteredExpirationDate);
  };

  const handleUnitCountChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === "" || /^\d+$/.test(value)) {
      setUnitCount(value);
    }
  };

  return (
    <Styles.ModalContainer>
      <Styles.Column>
        <Styles.ColumnTitle>{containerName} Details</Styles.ColumnTitle>

        <Styles.FormGroup>
          <Styles.FormLabel>{containerLabel}</Styles.FormLabel>
          <Input
            ref={containerIdInputRef}
            placeholder={`Scan or enter ${containerLabel}`}
            value={containerId}
            onChange={(e) => setContainerId(e.target.value)}
            size="large"
          />
        </Styles.FormGroup>

        <Styles.FormGroup>
          <Styles.FormLabel>SKU ID</Styles.FormLabel>
          <Input
            placeholder="Scan or enter SKU"
            value={skuId}
            onChange={(e) => setSkuId(e.target.value)}
            size="large"
            readOnly={lockSkuId}
          />
        </Styles.FormGroup>

        <Styles.FormGroup>
          <Styles.FormLabel>Quantity</Styles.FormLabel>
          <Input
            placeholder="Enter quantity"
            value={unitCount}
            onChange={handleUnitCountChange}
            size="large"
            readOnly={lockUnitCount}
          />
        </Styles.FormGroup>

        <Styles.FormGroup>
          <Styles.FormLabel>Expiry Date (optional)</Styles.FormLabel>
          <Input
            type="date"
            value={expirationDate}
            min={minExpiryDate}
            onChange={(e) => setExpirationDate(e.target.value)}
            size="large"
          />
          {isExpiryDateValid ? (
            <Styles.FieldInfo>
              Leave blank if this SKU is not expiry-tracked.
            </Styles.FieldInfo>
          ) : (
            <Styles.FieldError>{EXPIRY_DATE_ERROR}</Styles.FieldError>
          )}
        </Styles.FormGroup>
      </Styles.Column>

      <Styles.Column>
        <Styles.ColumnTitle>{containerName} Configuration</Styles.ColumnTitle>
        <Styles.ToteVisual>
          <Styles.ToteInner />
        </Styles.ToteVisual>
        <Styles.SlotLabel>Slot: {slotLabel}.</Styles.SlotLabel>
      </Styles.Column>

      <Styles.Column>
        <Styles.ColumnTitle>Confirm Placement</Styles.ColumnTitle>
        <Styles.PlacementContainer>
          <Styles.SlotVisual>
            <Styles.ToteDropElement
              className={animationStarted ? "animate" : ""}
            >
              {containerName.toUpperCase()}
            </Styles.ToteDropElement>
            <Styles.HighlightZone />
          </Styles.SlotVisual>

          <Styles.PlacementText>
            Place {containerNoun} into the highlighted slot.
          </Styles.PlacementText>

          {errorMessage && (
            <Styles.ErrorText>{errorMessage}</Styles.ErrorText>
          )}

          <Styles.DoneButton
            disabled={!isFormValid || submitting}
            loading={submitting}
            onClick={handleDone}
          >
            Done
          </Styles.DoneButton>

          <Styles.WarningText>
            Acknowledge only after ensuring the {containerNoun} is placed.
          </Styles.WarningText>
        </Styles.PlacementContainer>
      </Styles.Column>
    </Styles.ModalContainer>
  );
}
